import { DEFAULT_THEME, TLTheme } from 'tldraw'

/**
 * CSS variable overrides for one color mode. `tl` retints the editor canvas
 * wrapper (`.tl-container`); `tla` retints tldraw.com app shell colors. Applied
 * as inline styles at runtime and cleared when switching back to the default
 * theme.
 */
export interface UIThemeVariant {
	tl: Record<string, string>
	tla: Record<string, string>
}

interface UITheme {
	id: string
	name: string
	light: UIThemeVariant
	dark: UIThemeVariant
}

const THEMES: UITheme[] = [
	{
		id: 'solarized',
		name: 'Solarized',
		light: {
			tl: {
				'tl-color-background': '#fdf6e3',
				'tl-color-text': '#657b83',
				'tl-color-text-0': '#586e75',
				'tl-color-text-1': '#657b83',
				'tl-color-text-3': '#93a1a1',
				'tl-color-text-shadow': '#fdf6e3',
				'tl-color-panel': '#eee8d5',
				'tl-color-panel-contrast': '#fdf6e3',
				'tl-color-panel-overlay': 'hsla(44, 87%, 94%, 0.82)',
				'tl-color-panel-transparent': 'hsla(44, 87%, 94%, 0)',
				'tl-color-low': '#eee8d5',
				'tl-color-low-border': '#e6dfca',
				'tl-color-divider': '#d6cdb5',
				'tl-color-selected': '#268bd2',
				'tl-color-selected-contrast': '#fdf6e3',
				'tl-color-primary': '#268bd2',
				'tl-color-focus': '#268bd2',
				'tl-color-muted-none': 'hsla(44, 10%, 40%, 0)',
				'tl-color-muted-0': 'hsla(44, 10%, 40%, 0.02)',
				'tl-color-muted-1': 'hsla(44, 10%, 40%, 0.1)',
				'tl-color-muted-2': 'hsla(44, 10%, 40%, 0.043)',
				'tl-color-hint': 'hsla(44, 10%, 40%, 0.055)',
				'tl-color-overlay': 'hsla(44, 10%, 40%, 0.2)',
				'tl-color-tooltip': '#073642',
				'tl-color-success': '#859900',
				'tl-color-info': '#268bd2',
				'tl-color-warning': '#cb4b16',
				'tl-color-danger': '#dc322f',
				'tl-color-grid': '#93a1a1',
				'tl-color-brush-fill': 'hsla(44, 10%, 40%, 0.1)',
				'tl-color-brush-stroke': 'hsla(44, 10%, 40%, 0.25)',
				'tl-color-selection-fill': 'hsla(205, 69%, 49%, 0.24)',
				'tl-color-selection-stroke': '#268bd2',
				'tl-color-snap': '#dc322f',
				'tl-color-laser': '#dc322f',
			},
			tla: {
				'tla-color-sidebar': '#eee8d5',
				'tla-color-inactive': '#93a1a1',
				'tla-color-inactive-hover': '#839496',
				'tla-color-primary-hover': '#2d99e8',
				'tla-color-secondary': '#d6cdb5',
				'tla-color-secondary-hover': '#ded6c0',
				'tla-color-secondary-border': '#c0b89e',
			},
		},
		dark: {
			tl: {
				'tl-color-background': '#002b36',
				'tl-color-text': '#839496',
				'tl-color-text-0': '#93a1a1',
				'tl-color-text-1': '#839496',
				'tl-color-text-3': '#586e75',
				'tl-color-text-shadow': '#002b36',
				'tl-color-panel': '#073642',
				'tl-color-panel-contrast': '#0a4050',
				'tl-color-panel-overlay': 'hsla(192, 81%, 14%, 0.82)',
				'tl-color-panel-transparent': 'hsla(192, 81%, 14%, 0)',
				'tl-color-low': '#073642',
				'tl-color-low-border': '#0a4050',
				'tl-color-divider': '#0a4050',
				'tl-color-selected': '#268bd2',
				'tl-color-selected-contrast': '#fdf6e3',
				'tl-color-primary': '#268bd2',
				'tl-color-focus': '#268bd2',
				'tl-color-muted-none': 'hsla(187, 50%, 70%, 0)',
				'tl-color-muted-0': 'hsla(187, 50%, 70%, 0.02)',
				'tl-color-muted-1': 'hsla(187, 50%, 70%, 0.1)',
				'tl-color-muted-2': 'hsla(187, 50%, 70%, 0.05)',
				'tl-color-hint': 'hsla(187, 50%, 70%, 0.07)',
				'tl-color-overlay': 'hsla(192, 100%, 11%, 0.5)',
				'tl-color-tooltip': '#eee8d5',
				'tl-color-success': '#859900',
				'tl-color-info': '#268bd2',
				'tl-color-warning': '#cb4b16',
				'tl-color-danger': '#dc322f',
				'tl-color-grid': '#586e75',
				'tl-color-brush-fill': 'hsla(187, 50%, 70%, 0.05)',
				'tl-color-brush-stroke': 'hsla(187, 50%, 70%, 0.25)',
				'tl-color-selection-fill': 'hsla(205, 69%, 49%, 0.2)',
				'tl-color-selection-stroke': '#268bd2',
				'tl-color-snap': '#dc322f',
				'tl-color-laser': '#dc322f',
			},
			tla: {
				'tla-color-sidebar': '#00212b',
				'tla-color-inactive': '#586e75',
				'tla-color-inactive-hover': '#657b83',
				'tla-color-primary-hover': '#2d99e8',
				'tla-color-secondary': '#073642',
				'tla-color-secondary-hover': '#0a4050',
				'tla-color-secondary-border': '#0a4050',
			},
		},
	},
	{
		id: 'dracula',
		name: 'Dracula',
		light: {
			tl: {
				'tl-color-background': '#f8f8f2',
				'tl-color-text': '#282a36',
				'tl-color-text-0': '#44475a',
				'tl-color-text-1': '#44475a',
				'tl-color-text-3': '#6272a4',
				'tl-color-text-shadow': '#f8f8f2',
				'tl-color-panel': '#ffffff',
				'tl-color-panel-contrast': '#f8f8f2',
				'tl-color-panel-overlay': 'hsla(60, 30%, 96%, 0.82)',
				'tl-color-panel-transparent': 'hsla(60, 30%, 96%, 0)',
				'tl-color-low': '#ededea',
				'tl-color-low-border': '#e2e2de',
				'tl-color-divider': '#d8d8d4',
				'tl-color-selected': '#bd93f9',
				'tl-color-selected-contrast': '#ffffff',
				'tl-color-primary': '#bd93f9',
				'tl-color-focus': '#bd93f9',
				'tl-color-muted-none': 'hsla(231, 15%, 18%, 0)',
				'tl-color-muted-0': 'hsla(231, 15%, 18%, 0.02)',
				'tl-color-muted-1': 'hsla(231, 15%, 18%, 0.1)',
				'tl-color-muted-2': 'hsla(231, 15%, 18%, 0.043)',
				'tl-color-hint': 'hsla(231, 15%, 18%, 0.055)',
				'tl-color-overlay': 'hsla(231, 15%, 18%, 0.2)',
				'tl-color-tooltip': '#282a36',
				'tl-color-success': '#50fa7b',
				'tl-color-info': '#8be9fd',
				'tl-color-warning': '#ffb86c',
				'tl-color-danger': '#ff5555',
				'tl-color-grid': '#6272a4',
				'tl-color-brush-fill': 'hsla(231, 15%, 18%, 0.1)',
				'tl-color-brush-stroke': 'hsla(231, 15%, 18%, 0.25)',
				'tl-color-selection-fill': 'hsla(265, 89%, 78%, 0.24)',
				'tl-color-selection-stroke': '#bd93f9',
				'tl-color-snap': '#ff5555',
				'tl-color-laser': '#ff5555',
			},
			tla: {
				'tla-color-sidebar': '#ededea',
				'tla-color-inactive': '#6272a4',
				'tla-color-inactive-hover': '#7283b5',
				'tla-color-primary-hover': '#c9a5fa',
				'tla-color-secondary': '#d8d8d4',
				'tla-color-secondary-hover': '#e2e2de',
				'tla-color-secondary-border': '#c4c4c0',
			},
		},
		dark: {
			tl: {
				'tl-color-background': '#282a36',
				'tl-color-text': '#f8f8f2',
				'tl-color-text-0': '#f8f8f2',
				'tl-color-text-1': '#e0e0dc',
				'tl-color-text-3': '#6272a4',
				'tl-color-text-shadow': '#282a36',
				'tl-color-panel': '#44475a',
				'tl-color-panel-contrast': '#565971',
				'tl-color-panel-overlay': 'hsla(232, 14%, 31%, 0.82)',
				'tl-color-panel-transparent': 'hsla(232, 14%, 31%, 0)',
				'tl-color-low': '#343746',
				'tl-color-low-border': '#3d4051',
				'tl-color-divider': '#565971',
				'tl-color-selected': '#bd93f9',
				'tl-color-selected-contrast': '#ffffff',
				'tl-color-primary': '#bd93f9',
				'tl-color-focus': '#caa8fa',
				'tl-color-muted-none': 'hsla(60, 30%, 96%, 0)',
				'tl-color-muted-0': 'hsla(60, 30%, 96%, 0.02)',
				'tl-color-muted-1': 'hsla(60, 30%, 96%, 0.1)',
				'tl-color-muted-2': 'hsla(60, 30%, 96%, 0.05)',
				'tl-color-hint': 'hsla(60, 30%, 96%, 0.07)',
				'tl-color-overlay': 'hsla(231, 15%, 10%, 0.5)',
				'tl-color-tooltip': '#f8f8f2',
				'tl-color-success': '#50fa7b',
				'tl-color-info': '#8be9fd',
				'tl-color-warning': '#ffb86c',
				'tl-color-danger': '#ff5555',
				'tl-color-grid': '#44475a',
				'tl-color-brush-fill': 'hsla(60, 30%, 96%, 0.05)',
				'tl-color-brush-stroke': 'hsla(60, 30%, 96%, 0.25)',
				'tl-color-selection-fill': 'hsla(265, 89%, 78%, 0.2)',
				'tl-color-selection-stroke': '#bd93f9',
				'tl-color-snap': '#ff5555',
				'tl-color-laser': '#ff5555',
			},
			tla: {
				'tla-color-sidebar': '#21222c',
				'tla-color-inactive': '#6272a4',
				'tla-color-inactive-hover': '#7283b5',
				'tla-color-primary-hover': '#bd93f9',
				'tla-color-secondary': '#44475a',
				'tla-color-secondary-hover': '#4f5269',
				'tla-color-secondary-border': '#565971',
			},
		},
	},
	{
		id: 'nord',
		name: 'Nord',
		light: {
			tl: {
				'tl-color-background': '#eceff4',
				'tl-color-text': '#2e3440',
				'tl-color-text-0': '#3b4252',
				'tl-color-text-1': '#434c5e',
				'tl-color-text-3': '#7b88a1',
				'tl-color-text-shadow': '#eceff4',
				'tl-color-panel': '#e5e9f0',
				'tl-color-panel-contrast': '#eceff4',
				'tl-color-panel-overlay': 'hsla(218, 27%, 94%, 0.82)',
				'tl-color-panel-transparent': 'hsla(218, 27%, 94%, 0)',
				'tl-color-low': '#e5e9f0',
				'tl-color-low-border': '#d8dee9',
				'tl-color-divider': '#d0d6e1',
				'tl-color-selected': '#5e81ac',
				'tl-color-selected-contrast': '#eceff4',
				'tl-color-primary': '#5e81ac',
				'tl-color-focus': '#5e81ac',
				'tl-color-muted-none': 'hsla(220, 16%, 22%, 0)',
				'tl-color-muted-0': 'hsla(220, 16%, 22%, 0.02)',
				'tl-color-muted-1': 'hsla(220, 16%, 22%, 0.1)',
				'tl-color-muted-2': 'hsla(220, 16%, 22%, 0.043)',
				'tl-color-hint': 'hsla(220, 16%, 22%, 0.055)',
				'tl-color-overlay': 'hsla(220, 16%, 22%, 0.2)',
				'tl-color-tooltip': '#3b4252',
				'tl-color-success': '#a3be8c',
				'tl-color-info': '#81a1c1',
				'tl-color-warning': '#d08770',
				'tl-color-danger': '#bf616a',
				'tl-color-grid': '#7b88a1',
				'tl-color-brush-fill': 'hsla(220, 16%, 22%, 0.1)',
				'tl-color-brush-stroke': 'hsla(220, 16%, 22%, 0.25)',
				'tl-color-selection-fill': 'hsla(213, 32%, 52%, 0.24)',
				'tl-color-selection-stroke': '#5e81ac',
				'tl-color-snap': '#bf616a',
				'tl-color-laser': '#bf616a',
			},
			tla: {
				'tla-color-sidebar': '#e5e9f0',
				'tla-color-inactive': '#7b88a1',
				'tla-color-inactive-hover': '#8c97ae',
				'tla-color-primary-hover': '#6b8db5',
				'tla-color-secondary': '#d8dee9',
				'tla-color-secondary-hover': '#e0e4ec',
				'tla-color-secondary-border': '#c5cdd8',
			},
		},
		dark: {
			tl: {
				'tl-color-background': '#2e3440',
				'tl-color-text': '#d8dee9',
				'tl-color-text-0': '#e5e9f0',
				'tl-color-text-1': '#d8dee9',
				'tl-color-text-3': '#7b88a1',
				'tl-color-text-shadow': '#2e3440',
				'tl-color-panel': '#3b4252',
				'tl-color-panel-contrast': '#434c5e',
				'tl-color-panel-overlay': 'hsla(220, 17%, 27%, 0.82)',
				'tl-color-panel-transparent': 'hsla(220, 17%, 27%, 0)',
				'tl-color-low': '#353c4a',
				'tl-color-low-border': '#3b4252',
				'tl-color-divider': '#434c5e',
				'tl-color-selected': '#88c0d0',
				'tl-color-selected-contrast': '#2e3440',
				'tl-color-primary': '#88c0d0',
				'tl-color-focus': '#88c0d0',
				'tl-color-muted-none': 'hsla(218, 27%, 92%, 0)',
				'tl-color-muted-0': 'hsla(218, 27%, 92%, 0.02)',
				'tl-color-muted-1': 'hsla(218, 27%, 92%, 0.1)',
				'tl-color-muted-2': 'hsla(218, 27%, 92%, 0.05)',
				'tl-color-hint': 'hsla(218, 27%, 92%, 0.07)',
				'tl-color-overlay': 'hsla(220, 16%, 12%, 0.5)',
				'tl-color-tooltip': '#e5e9f0',
				'tl-color-success': '#a3be8c',
				'tl-color-info': '#81a1c1',
				'tl-color-warning': '#d08770',
				'tl-color-danger': '#bf616a',
				'tl-color-grid': '#434c5e',
				'tl-color-brush-fill': 'hsla(218, 27%, 92%, 0.05)',
				'tl-color-brush-stroke': 'hsla(218, 27%, 92%, 0.25)',
				'tl-color-selection-fill': 'hsla(193, 43%, 67%, 0.2)',
				'tl-color-selection-stroke': '#88c0d0',
				'tl-color-snap': '#bf616a',
				'tl-color-laser': '#bf616a',
			},
			tla: {
				'tla-color-sidebar': '#272d38',
				'tla-color-inactive': '#7b88a1',
				'tla-color-inactive-hover': '#8c97ae',
				'tla-color-primary-hover': '#88c0d0',
				'tla-color-secondary': '#3b4252',
				'tla-color-secondary-hover': '#434c5e',
				'tla-color-secondary-border': '#4c566a',
			},
		},
	},
	{
		id: 'monokai',
		name: 'Monokai',
		light: {
			tl: {
				'tl-color-background': '#fafafa',
				'tl-color-text': '#49483e',
				'tl-color-text-0': '#383830',
				'tl-color-text-1': '#49483e',
				'tl-color-text-3': '#8f908a',
				'tl-color-text-shadow': '#fafafa',
				'tl-color-panel': '#f0f0ec',
				'tl-color-panel-contrast': '#fafafa',
				'tl-color-panel-overlay': 'hsla(60, 5%, 96%, 0.82)',
				'tl-color-panel-transparent': 'hsla(60, 5%, 96%, 0)',
				'tl-color-low': '#f0f0ec',
				'tl-color-low-border': '#e5e5e0',
				'tl-color-divider': '#d6d6d0',
				'tl-color-selected': '#66d9ef',
				'tl-color-selected-contrast': '#f7f7f7',
				'tl-color-primary': '#66d9ef',
				'tl-color-focus': '#66d9ef',
				'tl-color-muted-none': 'hsla(60, 6%, 26%, 0)',
				'tl-color-muted-0': 'hsla(60, 6%, 26%, 0.02)',
				'tl-color-muted-1': 'hsla(60, 6%, 26%, 0.1)',
				'tl-color-muted-2': 'hsla(60, 6%, 26%, 0.043)',
				'tl-color-hint': 'hsla(60, 6%, 26%, 0.055)',
				'tl-color-overlay': 'hsla(60, 6%, 26%, 0.2)',
				'tl-color-tooltip': '#f7f7f7',
				'tl-color-success': '#a6e22e',
				'tl-color-info': '#66d9ef',
				'tl-color-warning': '#fd971f',
				'tl-color-danger': '#f92672',
				'tl-color-grid': '#8f908a',
				'tl-color-brush-fill': 'hsla(60, 6%, 26%, 0.1)',
				'tl-color-brush-stroke': 'hsla(60, 6%, 26%, 0.25)',
				'tl-color-selection-fill': 'hsla(190, 81%, 67%, 0.24)',
				'tl-color-selection-stroke': '#66d9ef',
				'tl-color-snap': '#f92672',
				'tl-color-laser': '#f92672',
			},
			tla: {
				'tla-color-sidebar': '#f0f0ec',
				'tla-color-inactive': '#8f908a',
				'tla-color-inactive-hover': '#9e9f9a',
				'tla-color-primary-hover': '#77ddf1',
				'tla-color-secondary': '#d6d6d0',
				'tla-color-secondary-hover': '#e0e0dc',
				'tla-color-secondary-border': '#c2c2bc',
			},
		},
		dark: {
			tl: {
				'tl-color-background': '#272822',
				'tl-color-text': '#f8f8f2',
				'tl-color-text-0': '#f8f8f2',
				'tl-color-text-1': '#e6e6e0',
				'tl-color-text-3': '#75715e',
				'tl-color-text-shadow': '#272822',
				'tl-color-panel': '#3e3d32',
				'tl-color-panel-contrast': '#49483e',
				'tl-color-panel-overlay': 'hsla(56, 10%, 22%, 0.82)',
				'tl-color-panel-transparent': 'hsla(56, 10%, 22%, 0)',
				'tl-color-low': '#2f302a',
				'tl-color-low-border': '#3e3d32',
				'tl-color-divider': '#49483e',
				'tl-color-selected': '#66d9ef',
				'tl-color-selected-contrast': '#272822',
				'tl-color-primary': '#66d9ef',
				'tl-color-focus': '#a1eff7',
				'tl-color-muted-none': 'hsla(60, 30%, 96%, 0)',
				'tl-color-muted-0': 'hsla(60, 30%, 96%, 0.02)',
				'tl-color-muted-1': 'hsla(60, 30%, 96%, 0.1)',
				'tl-color-muted-2': 'hsla(60, 30%, 96%, 0.05)',
				'tl-color-hint': 'hsla(60, 30%, 96%, 0.07)',
				'tl-color-overlay': 'hsla(70, 8%, 8%, 0.5)',
				'tl-color-tooltip': '#f8f8f2',
				'tl-color-success': '#a6e22e',
				'tl-color-info': '#66d9ef',
				'tl-color-warning': '#fd971f',
				'tl-color-danger': '#f92672',
				'tl-color-grid': '#49483e',
				'tl-color-brush-fill': 'hsla(60, 30%, 96%, 0.05)',
				'tl-color-brush-stroke': 'hsla(60, 30%, 96%, 0.25)',
				'tl-color-selection-fill': 'hsla(190, 81%, 67%, 0.2)',
				'tl-color-selection-stroke': '#66d9ef',
				'tl-color-snap': '#f92672',
				'tl-color-laser': '#f92672',
			},
			tla: {
				'tla-color-sidebar': '#1e1f1a',
				'tla-color-inactive': '#75715e',
				'tla-color-inactive-hover': '#8a8578',
				'tla-color-primary-hover': '#66d9ef',
				'tla-color-secondary': '#3e3d32',
				'tla-color-secondary-hover': '#49483e',
				'tla-color-secondary-border': '#49483e',
			},
		},
	},
	{
		id: 'gruvbox',
		name: 'Gruvbox',
		light: {
			tl: {
				'tl-color-background': '#fbf1c7',
				'tl-color-text': '#3c3836',
				'tl-color-text-0': '#282828',
				'tl-color-text-1': '#3c3836',
				'tl-color-text-3': '#928374',
				'tl-color-text-shadow': '#fbf1c7',
				'tl-color-panel': '#f2e5bc',
				'tl-color-panel-contrast': '#fbf1c7',
				'tl-color-panel-overlay': 'hsla(47, 87%, 88%, 0.82)',
				'tl-color-panel-transparent': 'hsla(47, 87%, 88%, 0)',
				'tl-color-low': '#f2e5bc',
				'tl-color-low-border': '#ebdbb2',
				'tl-color-divider': '#d5c4a1',
				'tl-color-selected': '#458588',
				'tl-color-selected-contrast': '#fbf1c7',
				'tl-color-primary': '#458588',
				'tl-color-focus': '#458588',
				'tl-color-muted-none': 'hsla(40, 10%, 30%, 0)',
				'tl-color-muted-0': 'hsla(40, 10%, 30%, 0.02)',
				'tl-color-muted-1': 'hsla(40, 10%, 30%, 0.1)',
				'tl-color-muted-2': 'hsla(40, 10%, 30%, 0.043)',
				'tl-color-hint': 'hsla(40, 10%, 30%, 0.055)',
				'tl-color-overlay': 'hsla(40, 10%, 30%, 0.2)',
				'tl-color-tooltip': '#282828',
				'tl-color-success': '#98971a',
				'tl-color-info': '#458588',
				'tl-color-warning': '#d79921',
				'tl-color-danger': '#cc241d',
				'tl-color-grid': '#928374',
				'tl-color-brush-fill': 'hsla(40, 10%, 30%, 0.1)',
				'tl-color-brush-stroke': 'hsla(40, 10%, 30%, 0.25)',
				'tl-color-selection-fill': 'hsla(194, 34%, 40%, 0.24)',
				'tl-color-selection-stroke': '#458588',
				'tl-color-snap': '#cc241d',
				'tl-color-laser': '#cc241d',
			},
			tla: {
				'tla-color-sidebar': '#f2e5bc',
				'tla-color-inactive': '#928374',
				'tla-color-inactive-hover': '#a39485',
				'tla-color-primary-hover': '#539698',
				'tla-color-secondary': '#d5c4a1',
				'tla-color-secondary-hover': '#ddd0b0',
				'tla-color-secondary-border': '#bdae93',
			},
		},
		dark: {
			tl: {
				'tl-color-background': '#282828',
				'tl-color-text': '#ebdbb2',
				'tl-color-text-0': '#fbf1c7',
				'tl-color-text-1': '#ebdbb2',
				'tl-color-text-3': '#928374',
				'tl-color-text-shadow': '#282828',
				'tl-color-panel': '#3c3836',
				'tl-color-panel-contrast': '#504945',
				'tl-color-panel-overlay': 'hsla(20, 5%, 22%, 0.82)',
				'tl-color-panel-transparent': 'hsla(20, 5%, 22%, 0)',
				'tl-color-low': '#32302f',
				'tl-color-low-border': '#3c3836',
				'tl-color-divider': '#504945',
				'tl-color-selected': '#83a598',
				'tl-color-selected-contrast': '#fbf1c7',
				'tl-color-primary': '#83a598',
				'tl-color-focus': '#83a598',
				'tl-color-muted-none': 'hsla(40, 35%, 80%, 0)',
				'tl-color-muted-0': 'hsla(40, 35%, 80%, 0.02)',
				'tl-color-muted-1': 'hsla(40, 35%, 80%, 0.1)',
				'tl-color-muted-2': 'hsla(40, 35%, 80%, 0.05)',
				'tl-color-hint': 'hsla(40, 35%, 80%, 0.07)',
				'tl-color-overlay': 'hsla(0, 0%, 8%, 0.5)',
				'tl-color-tooltip': '#ebdbb2',
				'tl-color-success': '#b8bb26',
				'tl-color-info': '#83a598',
				'tl-color-warning': '#fabd2f',
				'tl-color-danger': '#fb4934',
				'tl-color-grid': '#504945',
				'tl-color-brush-fill': 'hsla(40, 35%, 80%, 0.05)',
				'tl-color-brush-stroke': 'hsla(40, 35%, 80%, 0.25)',
				'tl-color-selection-fill': 'hsla(150, 17%, 58%, 0.2)',
				'tl-color-selection-stroke': '#83a598',
				'tl-color-snap': '#fb4934',
				'tl-color-laser': '#fb4934',
			},
			tla: {
				'tla-color-sidebar': '#1d2021',
				'tla-color-inactive': '#928374',
				'tla-color-inactive-hover': '#a39485',
				'tla-color-primary-hover': '#83a598',
				'tla-color-secondary': '#3c3836',
				'tla-color-secondary-hover': '#504945',
				'tla-color-secondary-border': '#504945',
			},
		},
	},
	{
		id: 'catppuccin',
		name: 'Catppuccin',
		light: {
			tl: {
				'tl-color-background': '#eff1f5',
				'tl-color-text': '#4c4f69',
				'tl-color-text-0': '#4c4f69',
				'tl-color-text-1': '#5c5f77',
				'tl-color-text-3': '#9ca0b0',
				'tl-color-text-shadow': '#eff1f5',
				'tl-color-panel': '#e6e9ef',
				'tl-color-panel-contrast': '#eff1f5',
				'tl-color-panel-overlay': 'hsla(220, 23%, 95%, 0.82)',
				'tl-color-panel-transparent': 'hsla(220, 23%, 95%, 0)',
				'tl-color-low': '#e6e9ef',
				'tl-color-low-border': '#dce0e8',
				'tl-color-divider': '#ccd0da',
				'tl-color-selected': '#8839ef',
				'tl-color-selected-contrast': '#eff1f5',
				'tl-color-primary': '#8839ef',
				'tl-color-focus': '#8839ef',
				'tl-color-muted-none': 'hsla(234, 16%, 35%, 0)',
				'tl-color-muted-0': 'hsla(234, 16%, 35%, 0.02)',
				'tl-color-muted-1': 'hsla(234, 16%, 35%, 0.1)',
				'tl-color-muted-2': 'hsla(234, 16%, 35%, 0.043)',
				'tl-color-hint': 'hsla(234, 16%, 35%, 0.055)',
				'tl-color-overlay': 'hsla(234, 16%, 35%, 0.2)',
				'tl-color-tooltip': '#4c4f69',
				'tl-color-success': '#40a02b',
				'tl-color-info': '#1e66f5',
				'tl-color-warning': '#fe640b',
				'tl-color-danger': '#d20f39',
				'tl-color-grid': '#9ca0b0',
				'tl-color-brush-fill': 'hsla(234, 16%, 35%, 0.1)',
				'tl-color-brush-stroke': 'hsla(234, 16%, 35%, 0.25)',
				'tl-color-selection-fill': 'hsla(267, 83%, 58%, 0.24)',
				'tl-color-selection-stroke': '#8839ef',
				'tl-color-snap': '#d20f39',
				'tl-color-laser': '#d20f39',
			},
			tla: {
				'tla-color-sidebar': '#e6e9ef',
				'tla-color-inactive': '#9ca0b0',
				'tla-color-inactive-hover': '#8c8fa2',
				'tla-color-primary-hover': '#9548f5',
				'tla-color-secondary': '#ccd0da',
				'tla-color-secondary-hover': '#d6dae4',
				'tla-color-secondary-border': '#bcc0cc',
			},
		},
		dark: {
			tl: {
				'tl-color-background': '#1e1e2e',
				'tl-color-text': '#cdd6f4',
				'tl-color-text-0': '#cdd6f4',
				'tl-color-text-1': '#bac2de',
				'tl-color-text-3': '#6c7086',
				'tl-color-text-shadow': '#1e1e2e',
				'tl-color-panel': '#313244',
				'tl-color-panel-contrast': '#45475a',
				'tl-color-panel-overlay': 'hsla(240, 21%, 23%, 0.82)',
				'tl-color-panel-transparent': 'hsla(240, 21%, 23%, 0)',
				'tl-color-low': '#28283a',
				'tl-color-low-border': '#313244',
				'tl-color-divider': '#45475a',
				'tl-color-selected': '#cba6f7',
				'tl-color-selected-contrast': '#1e1e2e',
				'tl-color-primary': '#cba6f7',
				'tl-color-focus': '#cba6f7',
				'tl-color-muted-none': 'hsla(226, 64%, 88%, 0)',
				'tl-color-muted-0': 'hsla(226, 64%, 88%, 0.02)',
				'tl-color-muted-1': 'hsla(226, 64%, 88%, 0.1)',
				'tl-color-muted-2': 'hsla(226, 64%, 88%, 0.05)',
				'tl-color-hint': 'hsla(226, 64%, 88%, 0.07)',
				'tl-color-overlay': 'hsla(240, 21%, 10%, 0.5)',
				'tl-color-tooltip': '#cdd6f4',
				'tl-color-success': '#a6e3a1',
				'tl-color-info': '#89b4fa',
				'tl-color-warning': '#fab387',
				'tl-color-danger': '#f38ba8',
				'tl-color-grid': '#45475a',
				'tl-color-brush-fill': 'hsla(226, 64%, 88%, 0.05)',
				'tl-color-brush-stroke': 'hsla(226, 64%, 88%, 0.25)',
				'tl-color-selection-fill': 'hsla(267, 84%, 81%, 0.2)',
				'tl-color-selection-stroke': '#cba6f7',
				'tl-color-snap': '#f38ba8',
				'tl-color-laser': '#f38ba8',
			},
			tla: {
				'tla-color-sidebar': '#181825',
				'tla-color-inactive': '#6c7086',
				'tla-color-inactive-hover': '#7f849c',
				'tla-color-primary-hover': '#cba6f7',
				'tla-color-secondary': '#313244',
				'tla-color-secondary-hover': '#45475a',
				'tla-color-secondary-border': '#45475a',
			},
		},
	},
	{
		id: 'one-dark',
		name: 'One Dark',
		light: {
			tl: {
				'tl-color-background': '#fafafa',
				'tl-color-text': '#383a42',
				'tl-color-text-0': '#383a42',
				'tl-color-text-1': '#4b4d55',
				'tl-color-text-3': '#a0a1a7',
				'tl-color-text-shadow': '#fafafa',
				'tl-color-panel': '#f0f0f0',
				'tl-color-panel-contrast': '#fafafa',
				'tl-color-panel-overlay': 'hsla(0, 0%, 98%, 0.82)',
				'tl-color-panel-transparent': 'hsla(0, 0%, 98%, 0)',
				'tl-color-low': '#f0f0f0',
				'tl-color-low-border': '#e5e5e6',
				'tl-color-divider': '#d4d4d5',
				'tl-color-selected': '#4078f2',
				'tl-color-selected-contrast': '#ffffff',
				'tl-color-primary': '#4078f2',
				'tl-color-focus': '#4078f2',
				'tl-color-muted-none': 'hsla(230, 8%, 24%, 0)',
				'tl-color-muted-0': 'hsla(230, 8%, 24%, 0.02)',
				'tl-color-muted-1': 'hsla(230, 8%, 24%, 0.1)',
				'tl-color-muted-2': 'hsla(230, 8%, 24%, 0.043)',
				'tl-color-hint': 'hsla(230, 8%, 24%, 0.055)',
				'tl-color-overlay': 'hsla(230, 8%, 24%, 0.2)',
				'tl-color-tooltip': '#383a42',
				'tl-color-success': '#50a14f',
				'tl-color-info': '#4078f2',
				'tl-color-warning': '#c18401',
				'tl-color-danger': '#e45649',
				'tl-color-grid': '#a0a1a7',
				'tl-color-brush-fill': 'hsla(230, 8%, 24%, 0.1)',
				'tl-color-brush-stroke': 'hsla(230, 8%, 24%, 0.25)',
				'tl-color-selection-fill': 'hsla(221, 87%, 60%, 0.24)',
				'tl-color-selection-stroke': '#4078f2',
				'tl-color-snap': '#e45649',
				'tl-color-laser': '#e45649',
			},
			tla: {
				'tla-color-sidebar': '#f0f0f0',
				'tla-color-inactive': '#a0a1a7',
				'tla-color-inactive-hover': '#8c8d93',
				'tla-color-primary-hover': '#5288f5',
				'tla-color-secondary': '#d4d4d5',
				'tla-color-secondary-hover': '#dedede',
				'tla-color-secondary-border': '#c4c4c5',
			},
		},
		dark: {
			tl: {
				'tl-color-background': '#282c34',
				'tl-color-text': '#abb2bf',
				'tl-color-text-0': '#dcdfe4',
				'tl-color-text-1': '#abb2bf',
				'tl-color-text-3': '#5c6370',
				'tl-color-text-shadow': '#282c34',
				'tl-color-panel': '#21252b',
				'tl-color-panel-contrast': '#2c313a',
				'tl-color-panel-overlay': 'hsla(220, 13%, 18%, 0.82)',
				'tl-color-panel-transparent': 'hsla(220, 13%, 18%, 0)',
				'tl-color-low': '#2c313a',
				'tl-color-low-border': '#333842',
				'tl-color-divider': '#3e4451',
				'tl-color-selected': '#61afef',
				'tl-color-selected-contrast': '#282c34',
				'tl-color-primary': '#61afef',
				'tl-color-focus': '#61afef',
				'tl-color-muted-none': 'hsla(219, 14%, 71%, 0)',
				'tl-color-muted-0': 'hsla(219, 14%, 71%, 0.02)',
				'tl-color-muted-1': 'hsla(219, 14%, 71%, 0.1)',
				'tl-color-muted-2': 'hsla(219, 14%, 71%, 0.05)',
				'tl-color-hint': 'hsla(219, 14%, 71%, 0.07)',
				'tl-color-overlay': 'hsla(220, 13%, 10%, 0.5)',
				'tl-color-tooltip': '#abb2bf',
				'tl-color-success': '#98c379',
				'tl-color-info': '#61afef',
				'tl-color-warning': '#e5c07b',
				'tl-color-danger': '#e06c75',
				'tl-color-grid': '#3e4451',
				'tl-color-brush-fill': 'hsla(219, 14%, 71%, 0.05)',
				'tl-color-brush-stroke': 'hsla(219, 14%, 71%, 0.25)',
				'tl-color-selection-fill': 'hsla(207, 82%, 66%, 0.2)',
				'tl-color-selection-stroke': '#61afef',
				'tl-color-snap': '#e06c75',
				'tl-color-laser': '#e06c75',
			},
			tla: {
				'tla-color-sidebar': '#21252b',
				'tla-color-inactive': '#5c6370',
				'tla-color-inactive-hover': '#6b727f',
				'tla-color-primary-hover': '#61afef',
				'tla-color-secondary': '#2c313a',
				'tla-color-secondary-hover': '#333842',
				'tla-color-secondary-border': '#3e4451',
			},
		},
	},
	{
		id: 'tokyo-night',
		name: 'Tokyo Night',
		light: {
			tl: {
				'tl-color-background': '#d5d6db',
				'tl-color-text': '#343b58',
				'tl-color-text-0': '#343b58',
				'tl-color-text-1': '#4c505e',
				'tl-color-text-3': '#9699a3',
				'tl-color-text-shadow': '#d5d6db',
				'tl-color-panel': '#cbccd1',
				'tl-color-panel-contrast': '#d5d6db',
				'tl-color-panel-overlay': 'hsla(230, 8%, 84%, 0.82)',
				'tl-color-panel-transparent': 'hsla(230, 8%, 84%, 0)',
				'tl-color-low': '#cbccd1',
				'tl-color-low-border': '#c0c1c7',
				'tl-color-divider': '#b4b5bc',
				'tl-color-selected': '#7aa2f7',
				'tl-color-selected-contrast': '#ffffff',
				'tl-color-primary': '#7aa2f7',
				'tl-color-focus': '#7aa2f7',
				'tl-color-muted-none': 'hsla(232, 25%, 27%, 0)',
				'tl-color-muted-0': 'hsla(232, 25%, 27%, 0.02)',
				'tl-color-muted-1': 'hsla(232, 25%, 27%, 0.1)',
				'tl-color-muted-2': 'hsla(232, 25%, 27%, 0.043)',
				'tl-color-hint': 'hsla(232, 25%, 27%, 0.055)',
				'tl-color-overlay': 'hsla(232, 25%, 27%, 0.2)',
				'tl-color-tooltip': '#343b58',
				'tl-color-success': '#9ece6a',
				'tl-color-info': '#7aa2f7',
				'tl-color-warning': '#e0af68',
				'tl-color-danger': '#f7768e',
				'tl-color-grid': '#9699a3',
				'tl-color-brush-fill': 'hsla(232, 25%, 27%, 0.1)',
				'tl-color-brush-stroke': 'hsla(232, 25%, 27%, 0.25)',
				'tl-color-selection-fill': 'hsla(218, 91%, 72%, 0.24)',
				'tl-color-selection-stroke': '#7aa2f7',
				'tl-color-snap': '#f7768e',
				'tl-color-laser': '#f7768e',
			},
			tla: {
				'tla-color-sidebar': '#cbccd1',
				'tla-color-inactive': '#9699a3',
				'tla-color-inactive-hover': '#86898f',
				'tla-color-primary-hover': '#8aacf8',
				'tla-color-secondary': '#b4b5bc',
				'tla-color-secondary-hover': '#c0c1c7',
				'tla-color-secondary-border': '#a5a6ae',
			},
		},
		dark: {
			tl: {
				'tl-color-background': '#1a1b26',
				'tl-color-text': '#a9b1d6',
				'tl-color-text-0': '#c0caf5',
				'tl-color-text-1': '#a9b1d6',
				'tl-color-text-3': '#565f89',
				'tl-color-text-shadow': '#1a1b26',
				'tl-color-panel': '#24283b',
				'tl-color-panel-contrast': '#2f3350',
				'tl-color-panel-overlay': 'hsla(235, 24%, 19%, 0.82)',
				'tl-color-panel-transparent': 'hsla(235, 24%, 19%, 0)',
				'tl-color-low': '#1f2030',
				'tl-color-low-border': '#24283b',
				'tl-color-divider': '#2f3350',
				'tl-color-selected': '#7aa2f7',
				'tl-color-selected-contrast': '#1a1b26',
				'tl-color-primary': '#7aa2f7',
				'tl-color-focus': '#7aa2f7',
				'tl-color-muted-none': 'hsla(226, 33%, 75%, 0)',
				'tl-color-muted-0': 'hsla(226, 33%, 75%, 0.02)',
				'tl-color-muted-1': 'hsla(226, 33%, 75%, 0.1)',
				'tl-color-muted-2': 'hsla(226, 33%, 75%, 0.05)',
				'tl-color-hint': 'hsla(226, 33%, 75%, 0.07)',
				'tl-color-overlay': 'hsla(235, 24%, 10%, 0.5)',
				'tl-color-tooltip': '#c0caf5',
				'tl-color-success': '#9ece6a',
				'tl-color-info': '#7dcfff',
				'tl-color-warning': '#e0af68',
				'tl-color-danger': '#f7768e',
				'tl-color-grid': '#2f3350',
				'tl-color-brush-fill': 'hsla(226, 33%, 75%, 0.05)',
				'tl-color-brush-stroke': 'hsla(226, 33%, 75%, 0.25)',
				'tl-color-selection-fill': 'hsla(218, 91%, 72%, 0.2)',
				'tl-color-selection-stroke': '#7aa2f7',
				'tl-color-snap': '#f7768e',
				'tl-color-laser': '#f7768e',
			},
			tla: {
				'tla-color-sidebar': '#16161e',
				'tla-color-inactive': '#565f89',
				'tla-color-inactive-hover': '#6b7394',
				'tla-color-primary-hover': '#7aa2f7',
				'tla-color-secondary': '#24283b',
				'tla-color-secondary-hover': '#2f3350',
				'tla-color-secondary-border': '#2f3350',
			},
		},
	},
	{
		id: 'everforest',
		name: 'Everforest',
		light: {
			tl: {
				'tl-color-background': '#fdf6e3',
				'tl-color-text': '#5c6a72',
				'tl-color-text-0': '#4b565c',
				'tl-color-text-1': '#5c6a72',
				'tl-color-text-3': '#939f91',
				'tl-color-text-shadow': '#fdf6e3',
				'tl-color-panel': '#f4ebd0',
				'tl-color-panel-contrast': '#fdf6e3',
				'tl-color-panel-overlay': 'hsla(47, 87%, 94%, 0.82)',
				'tl-color-panel-transparent': 'hsla(47, 87%, 94%, 0)',
				'tl-color-low': '#f4ebd0',
				'tl-color-low-border': '#eae1c8',
				'tl-color-divider': '#ddd5bd',
				'tl-color-selected': '#35a77c',
				'tl-color-selected-contrast': '#fdf6e3',
				'tl-color-primary': '#35a77c',
				'tl-color-focus': '#35a77c',
				'tl-color-muted-none': 'hsla(150, 10%, 38%, 0)',
				'tl-color-muted-0': 'hsla(150, 10%, 38%, 0.02)',
				'tl-color-muted-1': 'hsla(150, 10%, 38%, 0.1)',
				'tl-color-muted-2': 'hsla(150, 10%, 38%, 0.043)',
				'tl-color-hint': 'hsla(150, 10%, 38%, 0.055)',
				'tl-color-overlay': 'hsla(150, 10%, 38%, 0.2)',
				'tl-color-tooltip': '#2d353b',
				'tl-color-success': '#8da101',
				'tl-color-info': '#3a94c5',
				'tl-color-warning': '#dfa000',
				'tl-color-danger': '#f85552',
				'tl-color-grid': '#939f91',
				'tl-color-brush-fill': 'hsla(150, 10%, 38%, 0.1)',
				'tl-color-brush-stroke': 'hsla(150, 10%, 38%, 0.25)',
				'tl-color-selection-fill': 'hsla(158, 51%, 43%, 0.24)',
				'tl-color-selection-stroke': '#35a77c',
				'tl-color-snap': '#f85552',
				'tl-color-laser': '#f85552',
			},
			tla: {
				'tla-color-sidebar': '#f4ebd0',
				'tla-color-inactive': '#939f91',
				'tla-color-inactive-hover': '#828e80',
				'tla-color-primary-hover': '#3fb88b',
				'tla-color-secondary': '#ddd5bd',
				'tla-color-secondary-hover': '#e5dec9',
				'tla-color-secondary-border': '#cdc5af',
			},
		},
		dark: {
			tl: {
				'tl-color-background': '#2d353b',
				'tl-color-text': '#d3c6aa',
				'tl-color-text-0': '#e0d5bb',
				'tl-color-text-1': '#d3c6aa',
				'tl-color-text-3': '#7a8478',
				'tl-color-text-shadow': '#2d353b',
				'tl-color-panel': '#343f44',
				'tl-color-panel-contrast': '#3d484d',
				'tl-color-panel-overlay': 'hsla(195, 12%, 24%, 0.82)',
				'tl-color-panel-transparent': 'hsla(195, 12%, 24%, 0)',
				'tl-color-low': '#313b40',
				'tl-color-low-border': '#343f44',
				'tl-color-divider': '#3d484d',
				'tl-color-selected': '#83c092',
				'tl-color-selected-contrast': '#2d353b',
				'tl-color-primary': '#83c092',
				'tl-color-focus': '#83c092',
				'tl-color-muted-none': 'hsla(40, 28%, 73%, 0)',
				'tl-color-muted-0': 'hsla(40, 28%, 73%, 0.02)',
				'tl-color-muted-1': 'hsla(40, 28%, 73%, 0.1)',
				'tl-color-muted-2': 'hsla(40, 28%, 73%, 0.05)',
				'tl-color-hint': 'hsla(40, 28%, 73%, 0.07)',
				'tl-color-overlay': 'hsla(195, 14%, 12%, 0.5)',
				'tl-color-tooltip': '#d3c6aa',
				'tl-color-success': '#a7c080',
				'tl-color-info': '#7fbbb3',
				'tl-color-warning': '#dbbc7f',
				'tl-color-danger': '#e67e80',
				'tl-color-grid': '#3d484d',
				'tl-color-brush-fill': 'hsla(40, 28%, 73%, 0.05)',
				'tl-color-brush-stroke': 'hsla(40, 28%, 73%, 0.25)',
				'tl-color-selection-fill': 'hsla(138, 27%, 63%, 0.2)',
				'tl-color-selection-stroke': '#83c092',
				'tl-color-snap': '#e67e80',
				'tl-color-laser': '#e67e80',
			},
			tla: {
				'tla-color-sidebar': '#272e33',
				'tla-color-inactive': '#7a8478',
				'tla-color-inactive-hover': '#8a9488',
				'tla-color-primary-hover': '#83c092',
				'tla-color-secondary': '#343f44',
				'tla-color-secondary-hover': '#3d484d',
				'tla-color-secondary-border': '#3d484d',
			},
		},
	},
	{
		id: 'github',
		name: 'GitHub',
		light: {
			tl: {
				'tl-color-background': '#ffffff',
				'tl-color-text': '#1f2328',
				'tl-color-text-0': '#1f2328',
				'tl-color-text-1': '#31363b',
				'tl-color-text-3': '#656d76',
				'tl-color-text-shadow': '#ffffff',
				'tl-color-panel': '#f6f8fa',
				'tl-color-panel-contrast': '#ffffff',
				'tl-color-panel-overlay': 'hsla(0, 0%, 100%, 0.82)',
				'tl-color-panel-transparent': 'hsla(0, 0%, 100%, 0)',
				'tl-color-low': '#f6f8fa',
				'tl-color-low-border': '#eaeef2',
				'tl-color-divider': '#d0d7de',
				'tl-color-selected': '#0969da',
				'tl-color-selected-contrast': '#ffffff',
				'tl-color-primary': '#0969da',
				'tl-color-focus': '#0969da',
				'tl-color-muted-none': 'hsla(210, 13%, 16%, 0)',
				'tl-color-muted-0': 'hsla(210, 13%, 16%, 0.02)',
				'tl-color-muted-1': 'hsla(210, 13%, 16%, 0.1)',
				'tl-color-muted-2': 'hsla(210, 13%, 16%, 0.043)',
				'tl-color-hint': 'hsla(210, 13%, 16%, 0.055)',
				'tl-color-overlay': 'hsla(210, 13%, 16%, 0.2)',
				'tl-color-tooltip': '#1f2328',
				'tl-color-success': '#1a7f37',
				'tl-color-info': '#0969da',
				'tl-color-warning': '#bf8700',
				'tl-color-danger': '#cf222e',
				'tl-color-grid': '#656d76',
				'tl-color-brush-fill': 'hsla(210, 13%, 16%, 0.1)',
				'tl-color-brush-stroke': 'hsla(210, 13%, 16%, 0.25)',
				'tl-color-selection-fill': 'hsla(212, 92%, 45%, 0.24)',
				'tl-color-selection-stroke': '#0969da',
				'tl-color-snap': '#cf222e',
				'tl-color-laser': '#cf222e',
			},
			tla: {
				'tla-color-sidebar': '#f6f8fa',
				'tla-color-inactive': '#656d76',
				'tla-color-inactive-hover': '#57606a',
				'tla-color-primary-hover': '#1177e5',
				'tla-color-secondary': '#eaeef2',
				'tla-color-secondary-hover': '#f0f3f6',
				'tla-color-secondary-border': '#d0d7de',
			},
		},
		dark: {
			tl: {
				'tl-color-background': '#0d1117',
				'tl-color-text': '#c9d1d9',
				'tl-color-text-0': '#f0f6fc',
				'tl-color-text-1': '#c9d1d9',
				'tl-color-text-3': '#484f58',
				'tl-color-text-shadow': '#0d1117',
				'tl-color-panel': '#161b22',
				'tl-color-panel-contrast': '#21262d',
				'tl-color-panel-overlay': 'hsla(215, 21%, 11%, 0.82)',
				'tl-color-panel-transparent': 'hsla(215, 21%, 11%, 0)',
				'tl-color-low': '#12171e',
				'tl-color-low-border': '#161b22',
				'tl-color-divider': '#21262d',
				'tl-color-selected': '#58a6ff',
				'tl-color-selected-contrast': '#0d1117',
				'tl-color-primary': '#58a6ff',
				'tl-color-focus': '#58a6ff',
				'tl-color-muted-none': 'hsla(210, 29%, 80%, 0)',
				'tl-color-muted-0': 'hsla(210, 29%, 80%, 0.02)',
				'tl-color-muted-1': 'hsla(210, 29%, 80%, 0.1)',
				'tl-color-muted-2': 'hsla(210, 29%, 80%, 0.05)',
				'tl-color-hint': 'hsla(210, 29%, 80%, 0.07)',
				'tl-color-overlay': 'hsla(215, 28%, 7%, 0.5)',
				'tl-color-tooltip': '#c9d1d9',
				'tl-color-success': '#3fb950',
				'tl-color-info': '#58a6ff',
				'tl-color-warning': '#d29922',
				'tl-color-danger': '#f85149',
				'tl-color-grid': '#21262d',
				'tl-color-brush-fill': 'hsla(210, 29%, 80%, 0.05)',
				'tl-color-brush-stroke': 'hsla(210, 29%, 80%, 0.25)',
				'tl-color-selection-fill': 'hsla(212, 100%, 67%, 0.2)',
				'tl-color-selection-stroke': '#58a6ff',
				'tl-color-snap': '#f85149',
				'tl-color-laser': '#f85149',
			},
			tla: {
				'tla-color-sidebar': '#010409',
				'tla-color-inactive': '#484f58',
				'tla-color-inactive-hover': '#57606a',
				'tla-color-primary-hover': '#58a6ff',
				'tla-color-secondary': '#161b22',
				'tla-color-secondary-hover': '#21262d',
				'tla-color-secondary-border': '#21262d',
			},
		},
	},
	{
		id: 'flexoki',
		name: 'Flexoki',
		light: {
			tl: {
				'tl-color-background': '#fffcf0',
				'tl-color-text': '#403e3c',
				'tl-color-text-0': '#1c1b1a',
				'tl-color-text-1': '#403e3c',
				'tl-color-text-3': '#878580',
				'tl-color-text-shadow': '#fffcf0',
				'tl-color-panel': '#f2f0e5',
				'tl-color-panel-contrast': '#fffcf0',
				'tl-color-panel-overlay': 'hsla(45, 100%, 97%, 0.82)',
				'tl-color-panel-transparent': 'hsla(45, 100%, 97%, 0)',
				'tl-color-low': '#f2f0e5',
				'tl-color-low-border': '#e6e4d9',
				'tl-color-divider': '#dad8ce',
				'tl-color-selected': '#205ea6',
				'tl-color-selected-contrast': '#fffcf0',
				'tl-color-primary': '#205ea6',
				'tl-color-focus': '#205ea6',
				'tl-color-muted-none': 'hsla(36, 4%, 24%, 0)',
				'tl-color-muted-0': 'hsla(36, 4%, 24%, 0.02)',
				'tl-color-muted-1': 'hsla(36, 4%, 24%, 0.1)',
				'tl-color-muted-2': 'hsla(36, 4%, 24%, 0.043)',
				'tl-color-hint': 'hsla(36, 4%, 24%, 0.055)',
				'tl-color-overlay': 'hsla(36, 4%, 24%, 0.2)',
				'tl-color-tooltip': '#1c1b1a',
				'tl-color-success': '#66800b',
				'tl-color-info': '#205ea6',
				'tl-color-warning': '#ad8301',
				'tl-color-danger': '#af3029',
				'tl-color-grid': '#878580',
				'tl-color-brush-fill': 'hsla(36, 4%, 24%, 0.1)',
				'tl-color-brush-stroke': 'hsla(36, 4%, 24%, 0.25)',
				'tl-color-selection-fill': 'hsla(213, 68%, 39%, 0.24)',
				'tl-color-selection-stroke': '#205ea6',
				'tl-color-snap': '#af3029',
				'tl-color-laser': '#af3029',
			},
			tla: {
				'tla-color-sidebar': '#f2f0e5',
				'tla-color-inactive': '#878580',
				'tla-color-inactive-hover': '#6f6e69',
				'tla-color-primary-hover': '#286bb5',
				'tla-color-secondary': '#dad8ce',
				'tla-color-secondary-hover': '#e2e0d7',
				'tla-color-secondary-border': '#cecdc3',
			},
		},
		dark: {
			tl: {
				'tl-color-background': '#100f0f',
				'tl-color-text': '#cecdc3',
				'tl-color-text-0': '#ececec',
				'tl-color-text-1': '#cecdc3',
				'tl-color-text-3': '#575653',
				'tl-color-text-shadow': '#100f0f',
				'tl-color-panel': '#1c1b1a',
				'tl-color-panel-contrast': '#282726',
				'tl-color-panel-overlay': 'hsla(30, 7%, 10%, 0.82)',
				'tl-color-panel-transparent': 'hsla(30, 7%, 10%, 0)',
				'tl-color-low': '#161514',
				'tl-color-low-border': '#1c1b1a',
				'tl-color-divider': '#282726',
				'tl-color-selected': '#4385be',
				'tl-color-selected-contrast': '#fffcf0',
				'tl-color-primary': '#4385be',
				'tl-color-focus': '#4385be',
				'tl-color-muted-none': 'hsla(40, 5%, 79%, 0)',
				'tl-color-muted-0': 'hsla(40, 5%, 79%, 0.02)',
				'tl-color-muted-1': 'hsla(40, 5%, 79%, 0.1)',
				'tl-color-muted-2': 'hsla(40, 5%, 79%, 0.05)',
				'tl-color-hint': 'hsla(40, 5%, 79%, 0.07)',
				'tl-color-overlay': 'hsla(0, 0%, 4%, 0.5)',
				'tl-color-tooltip': '#cecdc3',
				'tl-color-success': '#879a39',
				'tl-color-info': '#4385be',
				'tl-color-warning': '#d0a215',
				'tl-color-danger': '#d14d41',
				'tl-color-grid': '#282726',
				'tl-color-brush-fill': 'hsla(40, 5%, 79%, 0.05)',
				'tl-color-brush-stroke': 'hsla(40, 5%, 79%, 0.25)',
				'tl-color-selection-fill': 'hsla(207, 47%, 50%, 0.2)',
				'tl-color-selection-stroke': '#4385be',
				'tl-color-snap': '#d14d41',
				'tl-color-laser': '#d14d41',
			},
			tla: {
				'tla-color-sidebar': '#0b0a0a',
				'tla-color-inactive': '#575653',
				'tla-color-inactive-hover': '#6f6e69',
				'tla-color-primary-hover': '#4385be',
				'tla-color-secondary': '#1c1b1a',
				'tla-color-secondary-hover': '#282726',
				'tla-color-secondary-border': '#282726',
			},
		},
	},
	{
		id: 'night-owl',
		name: 'Night Owl',
		light: {
			tl: {
				'tl-color-background': '#fbfbfb',
				'tl-color-text': '#403f53',
				'tl-color-text-0': '#403f53',
				'tl-color-text-1': '#5c5b6e',
				'tl-color-text-3': '#989fb1',
				'tl-color-text-shadow': '#fbfbfb',
				'tl-color-panel': '#f0f0f0',
				'tl-color-panel-contrast': '#fbfbfb',
				'tl-color-panel-overlay': 'hsla(0, 0%, 98%, 0.82)',
				'tl-color-panel-transparent': 'hsla(0, 0%, 98%, 0)',
				'tl-color-low': '#f0f0f0',
				'tl-color-low-border': '#e4e4e4',
				'tl-color-divider': '#d6d6d6',
				'tl-color-selected': '#2AA298',
				'tl-color-selected-contrast': '#ffffff',
				'tl-color-primary': '#2AA298',
				'tl-color-focus': '#2AA298',
				'tl-color-muted-none': 'hsla(241, 16%, 29%, 0)',
				'tl-color-muted-0': 'hsla(241, 16%, 29%, 0.02)',
				'tl-color-muted-1': 'hsla(241, 16%, 29%, 0.1)',
				'tl-color-muted-2': 'hsla(241, 16%, 29%, 0.043)',
				'tl-color-hint': 'hsla(241, 16%, 29%, 0.055)',
				'tl-color-overlay': 'hsla(241, 16%, 29%, 0.2)',
				'tl-color-tooltip': '#403f53',
				'tl-color-success': '#49d0c5',
				'tl-color-info': '#288ed7',
				'tl-color-warning': '#daaa01',
				'tl-color-danger': '#de3d3b',
				'tl-color-grid': '#989fb1',
				'tl-color-brush-fill': 'hsla(241, 16%, 29%, 0.1)',
				'tl-color-brush-stroke': 'hsla(241, 16%, 29%, 0.25)',
				'tl-color-selection-fill': 'hsla(175, 60%, 40%, 0.24)',
				'tl-color-selection-stroke': '#2AA298',
				'tl-color-snap': '#de3d3b',
				'tl-color-laser': '#de3d3b',
			},
			tla: {
				'tla-color-sidebar': '#f0f0f0',
				'tla-color-inactive': '#989fb1',
				'tla-color-inactive-hover': '#888fa0',
				'tla-color-primary-hover': '#32b5aa',
				'tla-color-secondary': '#d6d6d6',
				'tla-color-secondary-hover': '#e0e0e0',
				'tla-color-secondary-border': '#c4c4c4',
			},
		},
		dark: {
			tl: {
				'tl-color-background': '#011627',
				'tl-color-text': '#d6deeb',
				'tl-color-text-0': '#d6deeb',
				'tl-color-text-1': '#b0bec5',
				'tl-color-text-3': '#5f7e97',
				'tl-color-text-shadow': '#011627',
				'tl-color-panel': '#0b2942',
				'tl-color-panel-contrast': '#13344f',
				'tl-color-panel-overlay': 'hsla(207, 95%, 13%, 0.82)',
				'tl-color-panel-transparent': 'hsla(207, 95%, 13%, 0)',
				'tl-color-low': '#071e31',
				'tl-color-low-border': '#0b2942',
				'tl-color-divider': '#13344f',
				'tl-color-selected': '#7fdbca',
				'tl-color-selected-contrast': '#011627',
				'tl-color-primary': '#7fdbca',
				'tl-color-focus': '#7fdbca',
				'tl-color-muted-none': 'hsla(208, 44%, 84%, 0)',
				'tl-color-muted-0': 'hsla(208, 44%, 84%, 0.02)',
				'tl-color-muted-1': 'hsla(208, 44%, 84%, 0.1)',
				'tl-color-muted-2': 'hsla(208, 44%, 84%, 0.05)',
				'tl-color-hint': 'hsla(208, 44%, 84%, 0.07)',
				'tl-color-overlay': 'hsla(207, 95%, 6%, 0.5)',
				'tl-color-tooltip': '#d6deeb',
				'tl-color-success': '#addb67',
				'tl-color-info': '#82aaff',
				'tl-color-warning': '#ecc48d',
				'tl-color-danger': '#ef5350',
				'tl-color-grid': '#13344f',
				'tl-color-brush-fill': 'hsla(208, 44%, 84%, 0.05)',
				'tl-color-brush-stroke': 'hsla(208, 44%, 84%, 0.25)',
				'tl-color-selection-fill': 'hsla(166, 53%, 68%, 0.2)',
				'tl-color-selection-stroke': '#7fdbca',
				'tl-color-snap': '#ef5350',
				'tl-color-laser': '#ef5350',
			},
			tla: {
				'tla-color-sidebar': '#00101f',
				'tla-color-inactive': '#5f7e97',
				'tla-color-inactive-hover': '#708ea5',
				'tla-color-primary-hover': '#7fdbca',
				'tla-color-secondary': '#0b2942',
				'tla-color-secondary-hover': '#13344f',
				'tla-color-secondary-border': '#13344f',
			},
		},
	},
	{
		id: 'high-contrast',
		name: 'High Contrast',
		light: {
			tl: {
				'tl-color-background': '#ffffff',
				'tl-color-text': '#000000',
				'tl-color-text-0': '#000000',
				'tl-color-text-1': '#000000',
				'tl-color-text-3': '#555555',
				'tl-color-text-shadow': '#ffffff',
				'tl-color-panel': '#ffffff',
				'tl-color-panel-contrast': '#ffffff',
				'tl-color-panel-overlay': 'hsla(0, 0%, 100%, 0.82)',
				'tl-color-panel-transparent': 'hsla(0, 0%, 100%, 0)',
				'tl-color-low': '#f0f0f0',
				'tl-color-low-border': '#cccccc',
				'tl-color-divider': '#000000',
				'tl-color-selected': '#0000ff',
				'tl-color-selected-contrast': '#ffffff',
				'tl-color-primary': '#0000ff',
				'tl-color-focus': '#0000ff',
				'tl-color-muted-none': 'hsla(0, 0%, 0%, 0)',
				'tl-color-muted-0': 'hsla(0, 0%, 0%, 0.02)',
				'tl-color-muted-1': 'hsla(0, 0%, 0%, 0.1)',
				'tl-color-muted-2': 'hsla(0, 0%, 0%, 0.043)',
				'tl-color-hint': 'hsla(0, 0%, 0%, 0.055)',
				'tl-color-overlay': 'hsla(0, 0%, 0%, 0.2)',
				'tl-color-tooltip': '#000000',
				'tl-color-success': '#008000',
				'tl-color-info': '#0000ff',
				'tl-color-warning': '#cc7700',
				'tl-color-danger': '#cc0000',
				'tl-color-grid': '#999999',
				'tl-color-brush-fill': 'hsla(0, 0%, 0%, 0.1)',
				'tl-color-brush-stroke': 'hsla(0, 0%, 0%, 0.25)',
				'tl-color-selection-fill': 'hsla(240, 100%, 50%, 0.24)',
				'tl-color-selection-stroke': '#0000ff',
				'tl-color-snap': '#cc0000',
				'tl-color-laser': '#cc0000',
			},
			tla: {
				'tla-color-sidebar': '#f0f0f0',
				'tla-color-inactive': '#555555',
				'tla-color-inactive-hover': '#333333',
				'tla-color-primary-hover': '#3333ff',
				'tla-color-secondary': '#e0e0e0',
				'tla-color-secondary-hover': '#ebebeb',
				'tla-color-secondary-border': '#000000',
			},
		},
		dark: {
			tl: {
				'tl-color-background': '#000000',
				'tl-color-text': '#ffffff',
				'tl-color-text-0': '#ffffff',
				'tl-color-text-1': '#ffffff',
				'tl-color-text-3': '#aaaaaa',
				'tl-color-text-shadow': '#000000',
				'tl-color-panel': '#000000',
				'tl-color-panel-contrast': '#1a1a1a',
				'tl-color-panel-overlay': 'hsla(0, 0%, 0%, 0.82)',
				'tl-color-panel-transparent': 'hsla(0, 0%, 0%, 0)',
				'tl-color-low': '#0a0a0a',
				'tl-color-low-border': '#333333',
				'tl-color-divider': '#ffffff',
				'tl-color-selected': '#00ff00',
				'tl-color-selected-contrast': '#000000',
				'tl-color-primary': '#00ff00',
				'tl-color-focus': '#00ff00',
				'tl-color-muted-none': 'hsla(0, 0%, 100%, 0)',
				'tl-color-muted-0': 'hsla(0, 0%, 100%, 0.02)',
				'tl-color-muted-1': 'hsla(0, 0%, 100%, 0.1)',
				'tl-color-muted-2': 'hsla(0, 0%, 100%, 0.05)',
				'tl-color-hint': 'hsla(0, 0%, 100%, 0.07)',
				'tl-color-overlay': 'hsla(0, 0%, 0%, 0.5)',
				'tl-color-tooltip': '#ffffff',
				'tl-color-success': '#00ff00',
				'tl-color-info': '#00ccff',
				'tl-color-warning': '#ffcc00',
				'tl-color-danger': '#ff0000',
				'tl-color-grid': '#555555',
				'tl-color-brush-fill': 'hsla(0, 0%, 100%, 0.05)',
				'tl-color-brush-stroke': 'hsla(0, 0%, 100%, 0.25)',
				'tl-color-selection-fill': 'hsla(120, 100%, 50%, 0.2)',
				'tl-color-selection-stroke': '#00ff00',
				'tl-color-snap': '#ff0000',
				'tl-color-laser': '#ff0000',
			},
			tla: {
				'tla-color-sidebar': '#000000',
				'tla-color-inactive': '#aaaaaa',
				'tla-color-inactive-hover': '#cccccc',
				'tla-color-primary-hover': '#00ff00',
				'tla-color-secondary': '#1a1a1a',
				'tla-color-secondary-hover': '#333333',
				'tla-color-secondary-border': '#ffffff',
			},
		},
	},
]

/**
 * Derive a `TLTheme` from a UI theme by mapping CSS variable values onto the
 * canvas-facing fields of `TLThemeColors`. Shape palette colors (red, blue,
 * etc.) are inherited from `DEFAULT_THEME` so existing drawings keep their
 * colors when the theme changes.
 */
function buildTLTheme(def: UITheme): TLTheme {
	const map = (tl: Record<string, string>, fallback: TLTheme['colors']['light']) => ({
		...fallback,
		text: tl['tl-color-text'] ?? fallback.text,
		background: tl['tl-color-background'] ?? fallback.background,
		negativeSpace: tl['tl-color-background'] ?? fallback.negativeSpace,
		selectionStroke:
			tl['tl-color-selection-stroke'] ?? tl['tl-color-selected'] ?? fallback.selectionStroke,
		selectionFill: tl['tl-color-selection-fill'] ?? fallback.selectionFill,
		selectedContrast: tl['tl-color-selected-contrast'] ?? fallback.selectedContrast,
		brushFill: tl['tl-color-brush-fill'] ?? fallback.brushFill,
		brushStroke: tl['tl-color-brush-stroke'] ?? fallback.brushStroke,
		snap: tl['tl-color-snap'] ?? fallback.snap,
		laser: tl['tl-color-laser'] ?? fallback.laser,
	})

	return {
		...DEFAULT_THEME,
		id: def.id as TLTheme['id'],
		colors: {
			light: map(def.light.tl, DEFAULT_THEME.colors.light),
			dark: map(def.dark.tl, DEFAULT_THEME.colors.dark),
		},
	}
}

export const UI_THEMES: Array<UITheme & { theme: TLTheme }> = THEMES.map((def) => ({
	...def,
	theme: buildTLTheme(def),
}))
